/**
 * A bot-bait field for public, unauthenticated forms (T409).
 *
 * WHY THIS IS A PRIMITIVE RATHER THAN THREE LINES IN THE FORM. It is the one input in
 * the app that must NOT be `<Input>`: an `<Input>` renders a visible label, joins the
 * tab order and is announced by screen readers, which is the opposite of what bait is
 * for. Putting the raw element here — inside `app/components/ui/`, the sanctioned
 * primitive-owner scope — means the raw `<input>` lives in the one place the JSX-reuse
 * rule (R16) permits it. Every future public form gets the same implementation rather
 * than its own near-copy.
 *
 * WHY `type="text"` AND NOT `type="hidden"`. A hidden input is precisely the field a
 * scraper knows to skip — it is the standard carrier for framework state, so filling it
 * is a giveaway no bot author makes twice. Bait has to look fillable.
 *
 * ── THE FIELD MUST NOT LOOK LIKE ANYTHING A PASSWORD MANAGER WANTS TO FILL ──────────
 *
 * The first version of this component labelled itself "Website" with `name="_hp"`, and
 * that is close to the worst possible choice: "website" is a first-class field in
 * 1Password's and Safari's autofill heuristics, `autocomplete="off"` is widely ignored
 * by both, and `aria-hidden` + off-canvas positioning do not stop a password manager —
 * they only stop a human. A woman on iOS whose manager filled that field would have had
 * her testimonial silently discarded, and by design she would have seen the thank-you
 * screen and never known.
 *
 * So: a neutral name no heuristic matches, no autofill-attractive label, and the
 * explicit opt-out attributes that 1Password (`data-1p-ignore`), LastPass
 * (`data-lpignore`) and Bitwarden (`data-form-type="other"`) actually honour.
 *
 * The server side of the contract lives in the route action: a non-empty value must
 * return the SUCCESS shape, never an error — telling a scraper which of its submissions
 * were discarded is free tuning feedback. **That silence is exactly why the discard has
 * to be observable server-side**; the action emits a `honeypot_tripped` analytics event
 * so a wave of false positives shows up as a number instead of as nothing at all.
 */
export function HoneypotField({
  name = "confirm_reference",
  id = "tfc-hp",
}: {
  /** Form field name. The action reads the same key. Keep it autofill-uninteresting. */
  name?: string;
  id?: string;
}) {
  return (
    <div className="tfc-honeypot" aria-hidden="true">
      {/* Deliberately vague, and deliberately not a field name any autofill engine
          recognises. A human never sees it; a parser sees a plausible optional input. */}
      <label htmlFor={id}>Leave this field empty</label>
      <input
        id={id}
        name={name}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        defaultValue=""
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
      />
    </div>
  );
}
